import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { CATEGORIES } from "../lib/constants";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { SignOut, Check } from "@phosphor-icons/react";

export default function Profile() {
  const { user, loading, updateProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [selectedCats, setSelectedCats] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [brandsMap, setBrandsMap] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
    if (user) {
      setSelectedCats(user.favorite_categories || []);
      setSelectedBrands(user.favorite_brands || []);
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setBrandsMap(data.brands_by_category)).catch(() => {});
  }, []);

  const toggleCat = (id) => {
    setSelectedCats(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  };
  const toggleBrand = (b) => {
    setSelectedBrands(s => s.includes(b) ? s.filter(x => x !== b) : [...s, b]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateProfile({ favorite_categories: selectedCats, favorite_brands: selectedBrands });
      toast.success("Preferenze salvate");
    } catch { toast.error("Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const availableBrands = selectedCats.length > 0
    ? Array.from(new Set(selectedCats.flatMap(c => brandsMap[c] || []))).sort()
    : Array.from(new Set(Object.values(brandsMap).flat())).sort();

  if (!user) return null;

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-12 py-12">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-3 flex items-center gap-2">
            {user.is_premium && <span className="px-2 py-0.5 bg-primary text-white text-[9px] font-black uppercase tracking-widest">Premium</span>}
            Il tuo garage
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter" data-testid="profile-title">
            Ciao, {user.name}
          </h1>
          <p className="text-neutral-400 mt-2 text-sm">{user.email}</p>
          {user.is_premium && user.premium_until && (
            <p className="text-primary text-xs uppercase tracking-widest mt-2" data-testid="premium-badge">
              ★ Premium attivo fino al {new Date(user.premium_until).toLocaleDateString("it-IT")}
            </p>
          )}
        </div>
        <Button onClick={() => { logout(); navigate("/"); }} variant="outline" className="border-border rounded-sm text-xs uppercase tracking-wider font-bold" data-testid="profile-logout-btn">
          <SignOut size={14} weight="bold" className="mr-2" /> Logout
        </Button>
      </div>

      <div className="border border-border rounded-sm bg-card p-6 lg:p-10 mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-4">Categorie preferite</div>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map(c => (
            <button
              key={c.id}
              onClick={() => toggleCat(c.id)}
              data-testid={`fav-cat-${c.id}`}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border rounded-sm transition-colors ${selectedCats.includes(c.id) ? "bg-primary text-white border-primary" : "border-border text-neutral-300 hover:border-primary/50"}`}
            >{c.name}</button>
          ))}
        </div>
      </div>

      <div className="border border-border rounded-sm bg-card p-6 lg:p-10 mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-4">Marche preferite</div>
        <div className="flex flex-wrap gap-2 max-h-80 overflow-y-auto">
          {availableBrands.map(b => (
            <button
              key={b}
              onClick={() => toggleBrand(b)}
              data-testid={`fav-brand-${b}`}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border rounded-sm transition-colors ${selectedBrands.includes(b) ? "bg-primary text-white border-primary" : "border-border text-neutral-300 hover:border-primary/50"}`}
            >{b}</button>
          ))}
        </div>
      </div>

      <Button onClick={save} disabled={saving} data-testid="profile-save-btn" className="w-full sm:w-auto h-12 px-8 bg-primary hover:bg-primary/90 text-white font-black uppercase tracking-wider rounded-sm">
        {saving ? "Salvataggio..." : <><Check size={14} weight="bold" className="mr-2" /> Salva preferenze</>}
      </Button>
    </div>
  );
}
